import React, { useEffect, useState } from "react";
import "./ListFilter.css";
import Label from "./Label";

function flattenListItems<T extends { children?: T[] }>(items: T[]) {
    return items.reduce<T[]>(
        (prev, curr) => [...prev, curr, ...(curr.children || [])],
        [],
    );
}
export type ListFilterGroupUnitItem<T> = {
    data: T;
    selected: boolean;
    count: number;
    children?: ListFilterGroupUnitItem<T>[];
};

type ListFilterProps<T> = {
    title?: string;
    showAllFiltersWithoutCount?: boolean;
    items: ListFilterGroupUnitItem<T>[];
    onChange: (selected: T[]) => void;
};

function ListFilterGroupUnit<T extends { id: string; name: string }>(
    props: ListFilterProps<T>,
) {
    const { items, onChange, title, showAllFiltersWithoutCount } = props;

    const [filterState, setFilterState] = useState<{
        items: ListFilterGroupUnitItem<T>[];
        enabled: boolean;
    }>({
        items,
        enabled: !!flattenListItems<ListFilterGroupUnitItem<T>>(items).find(
            (i) => i.selected,
        ),
    });

    useEffect(() => {
        setFilterState({
            items,
            enabled: !!flattenListItems<ListFilterGroupUnitItem<T>>(items).find(
                (i) => i.selected,
            ),
        });
    }, [items]);

    function toggleParentFilter(parentId: string) {
        const newList = filterState.items.map((item) => {
            if (parentId !== item.data.id) return item;
            return {
                ...item,
                selected: !item.selected,
                children: item.children
                    ? item.children.map((it) => ({
                          ...it,
                          selected: !item.selected,
                      }))
                    : undefined,
            };
        });
        const flatNewList = flattenListItems<ListFilterGroupUnitItem<T>>(newList);
        const newEnabled = !!flatNewList.find((i) => i.selected);

        setFilterState({ items: newList, enabled: newEnabled });
        onChange(flatNewList.filter((it) => it.selected).map((it) => it.data));
    }

    function toggleSubFilter(newSelected: boolean, itemId: string, parentId: string) {
        const newList = filterState.items.map((item) => {
            if (parentId !== item.data.id) return item;
            return {
                ...item,
                selected: !newSelected ? false : item.selected,
                children: item.children
                    ? item.children.map((subItem) => {
                          if (itemId !== subItem.data.id) return subItem;
                          return { ...subItem, selected: newSelected };
                      })
                    : undefined,
            };
        });

        const flatNewList = flattenListItems<ListFilterGroupUnitItem<T>>(newList);
        const newEnabled = !!flatNewList.find((i) => i.selected);

        setFilterState({ items: newList, enabled: newEnabled });
        onChange(flatNewList.filter((it) => it.selected).map((it) => it.data));
    }

    return (
        <div className="list-filter__group-unit">
            <div className="list-filter__group-title">{title}</div>
            <div>
                {filterState.items.map(
                    (filter) =>
                        (filter.count !== 0 || showAllFiltersWithoutCount) && (
                            <React.Fragment key={filter.data.id}>
                                <button
                                    type="button"
                                    className="list-filter__list__item"
                                    onClick={() => {
                                        toggleParentFilter(filter.data.id);
                                    }}
                                >
                                    <div>
                                        <input
                                            type="checkbox"
                                            checked={filter.selected}
                                            readOnly
                                        />
                                        <Label.Medium>{filter.data.name}</Label.Medium>
                                    </div>
                                    {!showAllFiltersWithoutCount && filter.count > 0 ? (
                                        <span className="count">{filter.count}</span>
                                    ) : null}
                                </button>
                                {filter.children?.map((subFilter) => (
                                    <button
                                        type="button"
                                        key={subFilter.data.id}
                                        className="indent list-filter__list__item"
                                        onClick={() => {
                                            toggleSubFilter(
                                                !subFilter.selected,
                                                subFilter.data.id,
                                                filter.data.id,
                                            );
                                        }}
                                    >
                                        <div>
                                            <input
                                                type="checkbox"
                                                checked={subFilter.selected}
                                                readOnly
                                            />
                                            <Label.Medium>
                                                {subFilter.data.name}
                                            </Label.Medium>
                                        </div>
                                        {!showAllFiltersWithoutCount &&
                                        subFilter.count > 0 ? (
                                            <span className="count">
                                                {subFilter.count}
                                            </span>
                                        ) : null}
                                    </button>
                                ))}
                            </React.Fragment>
                        ),
                )}
            </div>
        </div>
    );
}

export default ListFilterGroupUnit;
